import { EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, take, takeUntil } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { ContentService } from '@core/services/content.service';
import { LoggingService } from '@core/services/logging.service';
import { ServerTokensService } from '@core/services/server-tokens.service';
import { SettingsService } from '@core/services/settings.service';
import { UrlHelperService } from '@core/services/url-helper.service';
import { PageViewModel as UmbracoPage } from '@models/api.models';

let hasInit: boolean = false;

// eslint-disable-next-line @typescript-eslint/naming-convention
export const PageResolveService: ResolveFn<UmbracoPage> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => {
    const unsubscribe: Subject<void> = new Subject<void>();
    const urlHelper = inject(UrlHelperService);
    const serverTokens = inject(ServerTokensService);
    const loggingService = inject(LoggingService);
    const settingsService = inject(SettingsService);
    const contentService = inject(ContentService);

    const resolveUrl = (url: string): Observable<UmbracoPage> => {
        const relativePathWithoutMarket = decodeURIComponent(
            url.split('?')[0].toLowerCase().replace(urlHelper.getCurrentMarket, '').split('/').filter(String).join('/')
        );

        return contentService.getContent(relativePathWithoutMarket);
    };

    const handlePageResponse = (page: Observable<UmbracoPage>): Observable<UmbracoPage> => {
        hasInit = true;
        return page.pipe(
            catchError(err => {
                return serverErrorPage(err);
            })
        );
    };

    const serverErrorPage = (err: HttpErrorResponse): Observable<UmbracoPage> => {
        loggingService.logMessage('Page Resolve Error', `${err.name}(${err.status}) - ${err.message}`);
        return settingsService.settings$.pipe(
            take(1),
            switchMap(settings => {
                let serverErrorUrl = settings.pageReferences?.serverErrorPage?.url;

                // TODO: https://jira.impact.dk/browse/KOP-2769 | this should be handled in umbraco instead
                if (err && err.status === 401) {
                    return of({ title: '401 Unauthorized', name: err.statusText, contentTypeAlias: 'errorPage' });
                }

                if (err && err.status === 404) {
                    serverErrorUrl = settings.pageReferences?.notFoundErrorPage?.url;
                }

                return serverErrorUrl ? contentService.getContent(serverErrorUrl) : EMPTY;
            }),
            takeUntil(unsubscribe)
        );
    };

    // Use response from SSR if available.
    if (!hasInit && serverTokens.pageResponse) {
        return handlePageResponse(of(serverTokens.pageResponse));
    }

    // Resolve by calling url from angular (after initial load / non-ssr mode)
    return handlePageResponse(resolveUrl(state.url));
};
